<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import {
  type ClientStats,
  type FailedClientItem,
  type NotExecutedClientItem,
  type UploadSummaryItem,
  type UploadSummaryMeta,
  uploadSummaryApi,
} from '@/api/entities/review/uploadSummary'

definePage({
  meta: {
    title: '업체별 업로드 현황',
    action: 'read',
    subject: 'review',
  },
})

const { handleError } = useErrorHandler()

// 상태
const loading = ref(false)
const statsLoading = ref(false)
const startDate = ref<string>('')
const endDate = ref<string>('')

// 페이지네이션
const page = ref(1)
const perPage = ref(20)
const perPageOptions = [10, 20, 50, 100, { title: '전체', value: -1 }]

// 정렬
const sortBy = ref<{ key: string; order: 'asc' | 'desc' }[]>([{ key: 'id', order: 'desc' }])

// 데이터
const items = ref<UploadSummaryItem[]>([])

const meta = ref<UploadSummaryMeta>({
  current_page: 1,
  per_page: 20,
  total: 0,
  last_page: 1,
})

// 업체 수 기준 통계
const clientStats = ref<ClientStats>({
  success_count: 0,
  failed_count: 0,
  not_executed_count: 0,
  ready_count: 0,
  pending_count: 0,
  total_clients: 0,
})

// 모달 상태
const failedClientsDialog = ref(false)
const notExecutedClientsDialog = ref(false)
const failedClients = ref<FailedClientItem[]>([])
const notExecutedClients = ref<NotExecutedClientItem[]>([])
const retryLoading = ref(false)

// 테이블 헤더
const headers = [
  { title: 'ID', key: 'id', sortable: true, width: '80px' },
  { title: '업체명', key: 'name', sortable: true },
  { title: '리뷰 서비스', key: 'upload_driver_name', sortable: false },
  { title: '총 건수', key: 'total_count', sortable: true, align: 'end' as const },
  { title: '성공', key: 'finished_count', sortable: true, align: 'end' as const },
  { title: '실패', key: 'failed_count', sortable: true, align: 'end' as const },
  { title: '대기', key: 'ready_count', sortable: true, align: 'end' as const },
  { title: '보류', key: 'pending_count', sortable: true, align: 'end' as const },
]

// 실패 업체 테이블 헤더
const failedClientsHeaders = [
  { title: '클라이언트 ID', key: 'client_id', width: '120px' },
  { title: '업체명', key: 'client_name' },
  { title: '실패 건수', key: 'failed_count', align: 'end' as const, width: '100px' },
  { title: '실패 사유', key: 'reasons' },
]

// 미실행 업체 테이블 헤더
const notExecutedClientsHeaders = [
  { title: '클라이언트 ID', key: 'client_id', width: '120px' },
  { title: '업체명', key: 'client_name' },
  { title: '대기 건수', key: 'ready_count', align: 'end' as const, width: '100px' },
  { title: '보류 건수', key: 'pending_count', align: 'end' as const, width: '100px' },
]

// 숫자 포맷
const formatNumber = (num: number | null | undefined) => {
  if (num === null || num === undefined)
    return '0'

  return new Intl.NumberFormat('ko-KR').format(num)
}

// 데이터 로드
const loadData = async () => {
  loading.value = true
  try {
    const params: Record<string, number | string> = {
      page: page.value,
      per_page: perPage.value,
    }

    if (startDate.value && endDate.value) {
      params.start_date = startDate.value
      params.end_date = endDate.value
    }

    // 정렬 파라미터
    if (sortBy.value.length > 0) {
      params.sort_by = sortBy.value[0].key
      params.sort_order = sortBy.value[0].order
    }

    const { success, data } = await uploadSummaryApi.getUploadSummary(params)

    if (success && data) {
      items.value = data.data
      meta.value = data.meta
    }
  }
  catch (error) {
    handleError(error, { silent: true })
  }
  finally {
    loading.value = false
  }
}

// 통합 통계 로드 (업체수 통계 + 실패업체 + 미실행업체)
const loadCombinedStats = async () => {
  statsLoading.value = true
  try {
    const params: Record<string, string> = {}

    if (startDate.value && endDate.value) {
      params.start_date = startDate.value
      params.end_date = endDate.value
    }

    const { success, data } = await uploadSummaryApi.getCombinedStats(params)

    if (success && data) {
      clientStats.value = data.stats
      failedClients.value = data.failed_clients
      notExecutedClients.value = data.not_executed_clients
    }
  }
  catch (error) {
    handleError(error, { silent: true })
  }
  finally {
    statsLoading.value = false
  }
}

// 실패 업체 모달 열기 (데이터는 이미 로드됨)
const openFailedClientsDialog = () => {
  failedClientsDialog.value = true
}

// 미실행 업체 모달 열기 (데이터는 이미 로드됨)
const openNotExecutedClientsDialog = () => {
  notExecutedClientsDialog.value = true
}

// 미실행 업체 전체 재시도
const retryNotExecutedClients = async () => {
  if (notExecutedClients.value.length === 0)
    return

  const confirmed = await new Promise<boolean>(resolve => {
    if (confirm(`${notExecutedClients.value.length}개 미실행 업체에 대해 이관을 재시도하시겠습니까?`))
      resolve(true)

    else
      resolve(false)
  })

  if (!confirmed)
    return

  retryLoading.value = true

  try {
    const clientIds = notExecutedClients.value.map(c => c.client_id)
    const { success, message } = await uploadSummaryApi.retryNotExecutedClients(clientIds)

    if (success) {
      alert(message || '재시도 요청이 완료되었습니다.')
      notExecutedClientsDialog.value = false

      // 통계 및 데이터 새로고침
      await Promise.all([loadCombinedStats(), loadData()])
    }
    else {
      alert(message || '재시도 요청 중 오류가 발생했습니다.')
    }
  }
  catch (error) {
    handleError(error)
  }
  finally {
    retryLoading.value = false
  }
}

// 페이지 변경
const onPageChange = (newPage: number) => {
  page.value = newPage
  loadData()
}

// per_page 변경
const onPerPageChange = () => {
  page.value = 1
  loadData()
}

// 정렬 변경
const onSortChange = (newSortBy: { key: string; order: 'asc' | 'desc' }[]) => {
  sortBy.value = newSortBy
  page.value = 1
  loadData()
}

// 날짜 변경 감시
watch([startDate, endDate], () => {
  if ((startDate.value && endDate.value) || (!startDate.value && !endDate.value)) {
    page.value = 1
    loadData()
    loadCombinedStats()
  }
})

// 전체 통계 계산 (페이지 내 건수)
const totalStats = computed(() => {
  return items.value.reduce(
    (acc, item) => {
      acc.total += item.total_count
      acc.finished += item.finished_count
      acc.failed += item.failed_count
      acc.ready += item.ready_count
      acc.pending += item.pending_count

      return acc
    },
    { total: 0, finished: 0, failed: 0, ready: 0, pending: 0 },
  )
})

onMounted(() => {
  loadData()
  loadCombinedStats()
})
</script>

<template>
  <div>
    <!-- 필터 섹션 -->
    <VCard class="mb-4">
      <VCardText>
        <VRow align="center">
          <VCol
            cols="12"
            md="3"
          >
            <VTextField
              v-model="startDate"
              type="date"
              label="시작일"
              density="compact"
              hide-details
            />
          </VCol>
          <VCol
            cols="12"
            md="3"
          >
            <VTextField
              v-model="endDate"
              type="date"
              label="종료일"
              density="compact"
              hide-details
            />
          </VCol>
          <VCol
            cols="12"
            md="2"
          >
            <VSelect
              v-model="perPage"
              :items="perPageOptions"
              label="표시 개수"
              density="compact"
              hide-details
              @update:model-value="onPerPageChange"
            />
          </VCol>
          <VCol
            cols="12"
            md="2"
          >
            <VBtn
              color="primary"
              @click="loadData(); loadCombinedStats()"
            >
              <VIcon
                start
                icon="tabler-refresh"
              />
              새로고침
            </VBtn>
          </VCol>
          <VCol
            cols="12"
            md="2"
            class="text-end"
          >
            <VChip
              color="info"
              variant="tonal"
            >
              정기이관 활성 업체: {{ meta.total }}개
            </VChip>
          </VCol>
        </VRow>
      </VCardText>
    </VCard>

    <!-- 업체 수 기준 통계 카드 -->
    <VCard class="mb-4">
      <VCardTitle>업체별 통계</VCardTitle>
      <VCardText>
        <VRow>
          <VCol
            cols="6"
            md="2"
          >
            <div class="text-caption text-success">
              성공업체
            </div>
            <div class="text-h6 text-success">
              {{ formatNumber(clientStats.success_count) }}개
            </div>
          </VCol>
          <VCol
            cols="6"
            md="2"
          >
            <VBtn
              variant="text"
              color="error"
              class="pa-0"
              :disabled="clientStats.failed_count === 0"
              @click="openFailedClientsDialog"
            >
              <div class="text-start">
                <div class="text-caption text-error">
                  실패업체
                </div>
                <div class="text-h6 text-error">
                  {{ formatNumber(clientStats.failed_count) }}개
                </div>
              </div>
            </VBtn>
          </VCol>
          <VCol
            cols="6"
            md="2"
          >
            <VBtn
              variant="text"
              color="grey"
              class="pa-0"
              :disabled="clientStats.not_executed_count === 0"
              @click="openNotExecutedClientsDialog"
            >
              <div class="text-start">
                <div class="text-caption text-grey">
                  미실행업체
                </div>
                <div class="text-h6 text-grey">
                  {{ formatNumber(clientStats.not_executed_count) }}개
                </div>
              </div>
            </VBtn>
          </VCol>
          <VCol
            cols="6"
            md="2"
          >
            <div class="text-caption text-warning">
              대기업체
            </div>
            <div class="text-h6 text-warning">
              {{ formatNumber(clientStats.ready_count) }}개
            </div>
          </VCol>
          <VCol
            cols="6"
            md="2"
          >
            <div class="text-caption text-info">
              보류업체
            </div>
            <div class="text-h6 text-info">
              {{ formatNumber(clientStats.pending_count) }}개
            </div>
          </VCol>
        </VRow>
      </VCardText>
    </VCard>

    <!-- 현재 페이지 건수 통계 -->
    <VCard class="mb-4">
      <VCardText>
        <VRow>
          <VCol
            cols="6"
            md="2"
          >
            <div class="text-caption text-medium-emphasis">
              페이지 내 총 건수
            </div>
            <div class="text-h6">
              {{ formatNumber(totalStats.total) }}
            </div>
          </VCol>
          <VCol
            cols="6"
            md="2"
          >
            <div class="text-caption text-success">
              성공
            </div>
            <div class="text-h6 text-success">
              {{ formatNumber(totalStats.finished) }}
            </div>
          </VCol>
          <VCol
            cols="6"
            md="2"
          >
            <div class="text-caption text-error">
              실패
            </div>
            <div class="text-h6 text-error">
              {{ formatNumber(totalStats.failed) }}
            </div>
          </VCol>
          <VCol
            cols="6"
            md="2"
          >
            <div class="text-caption text-warning">
              대기
            </div>
            <div class="text-h6 text-warning">
              {{ formatNumber(totalStats.ready) }}
            </div>
          </VCol>
          <VCol
            cols="6"
            md="2"
          >
            <div class="text-caption text-info">
              보류
            </div>
            <div class="text-h6 text-info">
              {{ formatNumber(totalStats.pending) }}
            </div>
          </VCol>
        </VRow>
      </VCardText>
    </VCard>

    <!-- 데이터 테이블 -->
    <VCard>
      <VCardTitle class="d-flex justify-space-between align-center">
        업체별 업로드 현황
      </VCardTitle>
      <VCardText>
        <VDataTableServer
          :headers="headers"
          :items="items"
          :items-length="meta.total"
          :loading="loading"
          :page="page"
          :items-per-page="perPage"
          :sort-by="sortBy"
          class="elevation-0"
          @update:page="onPageChange"
          @update:items-per-page="onPerPageChange"
          @update:sort-by="onSortChange"
        >
          <template #item.name="{ item }">
            <RouterLink
              :to="`/review/client/${item.id}`"
              class="text-decoration-none"
            >
              {{ item.name }}
            </RouterLink>
          </template>
          <template #item.upload_driver_name="{ item }">
            {{ item.upload_driver_name || '-' }}
          </template>
          <template #item.total_count="{ item }">
            {{ formatNumber(item.total_count) }}
          </template>
          <template #item.finished_count="{ item }">
            <span class="text-success">{{ formatNumber(item.finished_count) }}</span>
          </template>
          <template #item.failed_count="{ item }">
            <span :class="item.failed_count > 0 ? 'text-error' : ''">
              {{ formatNumber(item.failed_count) }}
            </span>
          </template>
          <template #item.ready_count="{ item }">
            <span :class="item.ready_count > 0 ? 'text-warning' : ''">
              {{ formatNumber(item.ready_count) }}
            </span>
          </template>
          <template #item.pending_count="{ item }">
            <span :class="item.pending_count > 0 ? 'text-info' : ''">
              {{ formatNumber(item.pending_count) }}
            </span>
          </template>
        </VDataTableServer>
      </VCardText>
    </VCard>

    <!-- 실패 업체 모달 -->
    <VDialog
      v-model="failedClientsDialog"
      max-width="1000"
    >
      <VCard>
        <VCardTitle>실패 업체 목록</VCardTitle>
        <VCardText>
          <VDataTable
            :headers="failedClientsHeaders"
            :items="failedClients"
            :loading="statsLoading"
            class="elevation-0"
          >
            <template #item.client_id="{ item }">
              <RouterLink
                :to="`/review/client/${item.client_id}`"
                class="text-decoration-none"
              >
                {{ item.client_id }}
              </RouterLink>
            </template>
            <template #item.client_name="{ item }">
              <RouterLink
                :to="`/review/client/${item.client_id}`"
                class="text-decoration-none"
              >
                {{ item.client_name }}
              </RouterLink>
            </template>
            <template #item.failed_count="{ item }">
              <span class="text-error">{{ formatNumber(item.failed_count) }}</span>
            </template>
            <template #item.reasons="{ item }">
              <div
                v-for="(reason, index) in item.reasons"
                :key="index"
                class="text-caption"
              >
                {{ reason }}
              </div>
            </template>
          </VDataTable>
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn
            variant="text"
            @click="failedClientsDialog = false"
          >
            확인
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- 미실행 업체 모달 -->
    <VDialog
      v-model="notExecutedClientsDialog"
      max-width="800"
    >
      <VCard>
        <VCardTitle>미실행 업체 목록</VCardTitle>
        <VCardText>
          <VDataTable
            :headers="notExecutedClientsHeaders"
            :items="notExecutedClients"
            :loading="statsLoading"
            class="elevation-0"
          >
            <template #item.client_id="{ item }">
              <RouterLink
                :to="`/review/client/${item.client_id}`"
                class="text-decoration-none"
              >
                {{ item.client_id }}
              </RouterLink>
            </template>
            <template #item.client_name="{ item }">
              <RouterLink
                :to="`/review/client/${item.client_id}`"
                class="text-decoration-none"
              >
                {{ item.client_name }}
              </RouterLink>
            </template>
            <template #item.ready_count="{ item }">
              <span class="text-warning">{{ formatNumber(item.ready_count) }}</span>
            </template>
            <template #item.pending_count="{ item }">
              <span class="text-info">{{ formatNumber(item.pending_count) }}</span>
            </template>
          </VDataTable>
        </VCardText>
        <VCardActions>
          <VBtn
            color="primary"
            :loading="retryLoading"
            :disabled="notExecutedClients.length === 0"
            @click="retryNotExecutedClients"
          >
            <VIcon
              start
              icon="tabler-refresh"
            />
            전체 재시도
          </VBtn>
          <VSpacer />
          <VBtn
            variant="text"
            @click="notExecutedClientsDialog = false"
          >
            확인
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </div>
</template>
